import { ButtonTertiary } from "@nova/component-btn"
import { Dropdown } from "@nova/component-dropdown"
import * as Icons from "@nova/component-icons"
import { EventCategory } from "@nova/schemas"
import { forwardRef, useImperativeHandle, useRef, useState } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { activeFilterCount } from "../activityFilters"
import {
    applyStagedFilters,
    clearStagedFilters,
    discardStagedFilters,
    selectAppliedFilters,
    selectStagedFilters,
} from "../slice"
import { CategoryFilterView } from "./CategoryFilterView"
import { DateFilterView } from "./DateFilterView"
import { categoryLabels, categoryOrder, datePresetLabel } from "./filterLabels"
import { FilterMenuShell, FilterOptionRow } from "./FilterMenuShell"
import { LayersFilterView } from "./LayersFilterView"
import { PeopleFilterView } from "./PeopleFilterView"

export type View =
    | { name: "root" }
    | { name: "date" }
    | { name: "people" }
    | { name: "category"; category: EventCategory }

export type ActivityFilterMenuRef = {
    openTo: (view: View) => void
}

const dateSummary = (filters: ReturnType<typeof selectStagedFilters>) =>
    "preset" in filters.date
        ? datePresetLabel(filters.date.preset)
        : `${filters.date.from} to ${filters.date.to}`

const RootView = ({
    onOpen,
    onCancel,
    onApply,
}: {
    onOpen: (view: View) => void
    onCancel: () => void
    onApply: () => void
}) => {
    const dispatch = useAppDispatch()
    const staged = useAppSelector(selectStagedFilters)

    return (
        <FilterMenuShell
            title="Filters"
            onCancel={onCancel}
            onApply={onApply}
            onClearAll={
                activeFilterCount(staged) > 0 ? () => dispatch(clearStagedFilters()) : undefined
            }
        >
            <FilterOptionRow
                label={`Date · ${dateSummary(staged)}`}
                onClick={() => onOpen({ name: "date" })}
                navigable
            />
            <FilterOptionRow
                label={staged.people.length > 0 ? `People · ${staged.people.length}` : "People"}
                onClick={() => onOpen({ name: "people" })}
                navigable
            />

            {categoryOrder.map((category) => {
                const selection = staged.categories[category]
                const selectedCount = selection
                    ? selection.actions.length + selection.subjectTypes.length
                    : 0
                const label = selectedCount
                    ? `${categoryLabels[category]} · ${selectedCount}`
                    : categoryLabels[category]

                return (
                    <FilterOptionRow
                        key={category}
                        label={label}
                        onClick={() => onOpen({ name: "category", category })}
                        navigable
                    />
                )
            })}
        </FilterMenuShell>
    )
}

export const ActivityFilterMenu = forwardRef<ActivityFilterMenuRef>((_props, ref) => {
    const dispatch = useAppDispatch()
    const applied = useAppSelector(selectAppliedFilters)
    const [view, setView] = useState<View>({ name: "root" })
    const triggerRef = useRef<HTMLDivElement>(null)

    const appliedCount = activeFilterCount(applied)

    const reset = () => setView({ name: "root" })
    const back = () => setView({ name: "root" })

    useImperativeHandle(
        ref,
        () => ({
            openTo: (target) => {
                setView(target)
                if (triggerRef.current?.dataset["state"] !== "open") triggerRef.current?.click()
            },
        }),
        []
    )

    return (
        <Dropdown
            onOpenChange={(open) => {
                if (open) return
                dispatch(discardStagedFilters())
                reset()
            }}
        >
            <Dropdown.Trigger as="div" className="group" ref={triggerRef}>
                <ButtonTertiary
                    title="Filters"
                    LeftIcon={Icons.FilterList}
                    RightIcon={appliedCount > 0 ? undefined : Icons.ArrowDropdown}
                    rightIconClassName="transition-transform group-data-[state=open]:rotate-180"
                    badge={
                        appliedCount > 0 && (
                            <div className="px-1.5 py-px rounded-md bg-blue-700 text-gray-0 flex items-center justify-center">
                                <small>{appliedCount}</small>
                            </div>
                        )
                    }
                />
            </Dropdown.Trigger>
            <Dropdown.Menu className="overflow-hidden rounded-lg">
                <FilterViews view={view} setView={setView} onBack={back} />
            </Dropdown.Menu>
        </Dropdown>
    )
})

ActivityFilterMenu.displayName = "ActivityFilterMenu"

const FilterViews = ({
    view,
    setView,
    onBack,
}: {
    view: View
    setView: (view: View) => void
    onBack: () => void
}) => {
    const dispatch = useAppDispatch()

    const apply = () => dispatch(applyStagedFilters())
    const cancel = () => {
        dispatch(discardStagedFilters())
        setView({ name: "root" })
    }

    if (view.name === "date") {
        return <DateFilterView onBack={onBack} onCancel={cancel} onApply={apply} />
    }

    if (view.name === "people") {
        return <PeopleFilterView onBack={onBack} onCancel={cancel} onApply={apply} />
    }

    if (view.name === "category" && view.category === EventCategory.Layers) {
        return <LayersFilterView onBack={onBack} onCancel={cancel} onApply={apply} />
    }

    if (view.name === "category") {
        return (
            <CategoryFilterView
                category={view.category}
                onBack={onBack}
                onCancel={cancel}
                onApply={apply}
            />
        )
    }

    return <RootView onOpen={setView} onCancel={cancel} onApply={apply} />
}
